// ── Party colours ─────────────────────────────────────────────────────────────
// How different two colours look is measured the way people see them (CIE Lab ΔE), not by raw
// red/green/blue: two blues 80 RGB steps apart can still look the same on a map. Every player at a
// table must be at least MIN_DISTANCE apart. Humans choose first; AI seats adapt to them.

export interface NamedColour { colour: string; name: string }

export const PALETTE: NamedColour[] = [
  { colour: "#e84393", name: "Pink" }, { colour: "#6c5ce7", name: "Purple" }, { colour: "#0984e3", name: "Blue" },
  { colour: "#00b894", name: "Green" }, { colour: "#e17055", name: "Coral" }, { colour: "#fdcb6e", name: "Yellow" },
  { colour: "#d63031", name: "Red" },
];
/** Extra colours the AI seats may take when a human's custom colour rules out the palette ones. */
const AI_EXTRA = ["#a3cb38", "#00cec9", "#8e5a2b", "#b2bec3", "#fd79a8", "#e67e22"];

/** Palette pairs: Coral/Red 25 and Purple/Blue 40 read as "the same colour" on the board, so they
 *  can't share a table; Coral/Yellow 46 and Pink/Red 49 are clearly different. */
export const MIN_DISTANCE = 45;

function lab(hex: string): [number, number, number] {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((c) => (c > 0.04045 ? ((c + 0.055) / 1.055) ** 2.4 : c / 12.92));
  const x = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047, y = r * 0.2126 + g * 0.7152 + b * 0.0722, z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883;
  const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  return [116 * f(y) - 16, 500 * (f(x) - f(y)), 200 * (f(y) - f(z))];
}

export function colourDistance(a: string, b: string): number {
  const [p, q] = [lab(a), lab(b)];
  return Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]);
}
export const distinct = (a: string, b: string): boolean => colourDistance(a, b) >= MIN_DISTANCE;

/** The taken colour (another human's) that `c` is too close to, if any. */
export function clashWith(c: string, taken: NamedColour[]): NamedColour | undefined {
  return taken.find((t) => !distinct(c, t.colour));
}

/**
 * Colours for the AI seats, given the humans' colours. Each AI keeps its current colour if it stands
 * apart from the humans and the AIs before it; otherwise it takes the first colour that does —
 * `prefer` first (e.g. the colour a human just gave up), then the palette, then the extras. If
 * nothing is far enough it takes whichever is furthest from everyone.
 */
export function assignAiColours(humans: string[], current: (string | undefined)[], prefer: string[] = []): string[] {
  const used = [...humans];
  const ok = (c: string) => used.every((u) => distinct(c, u));
  const pool = [...prefer, ...PALETTE.map((p) => p.colour), ...AI_EXTRA];
  return current.map((cur) => {
    let c = cur && ok(cur) ? cur : pool.find(ok);
    if (!c) c = pool.reduce((best, x) => (Math.min(...used.map((u) => colourDistance(x, u))) > Math.min(...used.map((u) => colourDistance(best, u))) ? x : best));
    used.push(c);
    return c;
  });
}
